import { onlineManager } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import {
  createPersister,
  createQueryClient,
  PERSIST_MAX_AGE,
  resumeQueuedReceipts,
  shouldPersistMutation,
} from './api/queryClient';
import { App } from './app/App';
import { ToastProvider } from './components/ui/Toast';
import './index.css';
import { LocaleProvider } from './lib/i18n';
import { applyTheme } from './lib/theme';

applyTheme();

async function start() {
  if (import.meta.env.MODE === 'mock') {
    const { startMockWorker } = await import('./mocks/browser');
    await startMockWorker();
  }
  const queryClient = createQueryClient();
  const persister = createPersister(__APP_VERSION__);
  const root = document.getElementById('root');
  if (!root) throw new Error('#root missing');

  createRoot(root).render(
    <StrictMode>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: PERSIST_MAX_AGE,
          buster: __APP_VERSION__,
          // Queued mutations, and failed ones still offering Retry / Discard.
          dehydrateOptions: { shouldDehydrateMutation: shouldPersistMutation },
        }}
        onSuccess={() => {
          // Replay expenses, payments and inbox actions queued while offline.
          if (onlineManager.isOnline()) void queryClient.resumePausedMutations();
          // Receipt photos whose upload failed (or whose mutation was dropped) last time.
          void resumeQueuedReceipts(queryClient);
        }}
      >
        <LocaleProvider>
          <ToastProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </ToastProvider>
        </LocaleProvider>
      </PersistQueryClientProvider>
    </StrictMode>,
  );
}

void start();
